import React from 'react';
import PropTypes from 'prop-types';
import Style from './ratefees.module.css';
import { CurrencyInputLabel, Input, Form, Icon } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

export function RateFee({ fee, currency, onChange, displayExclVat, displayExclVatLabel = false, includeTaxInPrice }) {
    const { t } = useTranslation();
    const locale = i18n.language;
    const taxlessFee = React.useMemo(() => includeTaxInPrice(false, fee.amount), [fee, includeTaxInPrice]);

    return (
        <div className={`${Style['fee-container']} ${displayExclVat ? Style['fee-container-with-taxes'] : ''}`}>
            <Form.Field className={Style['name-container']}>
                {(displayExclVat || displayExclVatLabel) && (
                    <div className={Style['label-and-delete']}>
                        <Form.Label label={t('ticket.rates.infos.fees.name')} />
                        <Icon className={Style['delete-in-label']} name="trash" margin={false} onClick={() => onChange(null)} />
                    </div>
                )}
                <Input
                    autoFocus
                    className={Style['name']}
                    value={fee.name || ''}
                    onChange={({ target: { value } }) => {
                        if (value.length >= 60) {
                            // max length
                            return;
                        }
                        onChange({ name: value });
                    }}
                    placeholder={t('ticket.rates.infos.fees.name')}
                />
            </Form.Field>
            <div className={Style['fee-price-container']}>
                <CurrencyInputLabel
                    {...(displayExclVat || (displayExclVatLabel && { label: t('ticket.services.form.content.price') }))}
                    currency={currency}
                    max={1000}
                    value={fee.amount || 0}
                    onChange={({ value }) => onChange({ amount: value, amount_no_tax: includeTaxInPrice(false, value) })}
                    locale={locale}
                />
                {displayExclVat && (
                    <CurrencyInputLabel
                        label={t('ticket.services.form.content.priceNoTaxes')}
                        currency={currency}
                        max={1000}
                        value={taxlessFee || 0}
                        onChange={({ value }) => onChange({ amount: includeTaxInPrice(true, value), amount_no_tax: value })}
                        locale={locale}
                    />
                )}
                <Icon className={Style['delete']} name="trash" margin={false} onClick={() => onChange(null)} />
                <div className={Style['delete-label']} onClick={() => onChange(null)}>
                    {t('ticket.common.action.delete')}
                </div>
            </div>
        </div>
    );
}

RateFee.propTypes = {
    fee: PropTypes.object,
    onChange: PropTypes.func,
    indice: PropTypes.number
};

export default function RateFees({ fees, onChange, currency, onChangeForm, displayExclVat, includeTaxInPrice }) {
    const { t } = useTranslation();

    const handleChange = React.useCallback(
        (fee_array_indice, fee_attribute = null) => {
            let tmp_fees = [...fees];
            // if fees_attribute is null, delete fee from array, else replace the value
            if (fee_attribute) {
                tmp_fees[fee_array_indice] = { ...tmp_fees[fee_array_indice], ...fee_attribute };
            } else {
                tmp_fees.splice(fee_array_indice, 1);
            }
            onChange(tmp_fees);
        },
        [onChange, fees]
    );

    return (
        <>
            <div className={Style['fees-cta-container']}>
                <div>{t('ticket.rates.infos.extra-fees')}</div>
            </div>
            <div className={Style['parent-container']}>
                <div className={Style['container']}>
                    <div className={Style['fees']}>
                        {fees.map((fee, i) => (
                            <RateFee
                                key={i}
                                fee={fee}
                                currency={currency}
                                onChange={value => handleChange(i, value)}
                                displayExclVat={displayExclVat}
                                includeTaxInPrice={includeTaxInPrice}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
}

RateFees.propTypes = {
    fees: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    price: PropTypes.number,
    rateType: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    onChangeForm: PropTypes.func.isRequired
};
